<template>
  <div class="px-2 py-1">
    <FormInput
      type="search"
      v-model="search"
      :placeholder="placeholder"
    />
    <p class="mt-3 mb-2 text-xs font-bold">Recommended</p>
    <div class="relative h-44 overflow-y-auto">
      <FormCheckboxes
        v-if="searchResults.length"
        :model-value="modelValue"
        :options="searchResults"
        @load-next-page="() => handleSearch(currentPage + 1)"
        @update:modelValue="updateModelValue"
      />
      <AnimationLoadingDotsHorizontal v-if="loading" class="absolute inset-0 mx-auto flex items-center" />
      <FormButton v-if="allowSupportRequest && !loading && searchResults.length < 5 && store.isLoggedIn" size="md"
                  @click="openSupportRequest" color="secondary">
        Request a new option →
      </FormButton>
    </div>
    <p class="mt-4 mb-2 text-xs font-bold">Selected</p>
    <FormCheckboxes
      v-if="modelValue.length"
      :model-value="modelValue"
      :options="modelValue"
      @update:modelValue="updateModelValue"
    />
    <p v-else class="mt-2 mb-2 text-xs text-slate-300">No options selected</p>
    <SupportRequestModal v-model:open="showSupportRequestModal" :type="supportRequestType"/>
  </div>
</template>

<script setup>
import {debounce} from "lodash";
import {onMounted, ref, watch} from "vue";
import AnimationLoadingDotsHorizontal from "./AnimationLoadingDotsHorizontal.vue";
import FormInput from "./FormInput.vue";
import FormCheckboxes from "./FormCheckboxes.vue";
import SupportRequestModal from "./SupportRequestModal.vue";
import FormButton from "./FormButton.vue";
import {useAccountStore} from "../stores/AccountStore.js";

const emits = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  getOptions: {
    type: Function,
    default: () => {
    },
  },
  recommendedOptions: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Search...",
  },
  maxAllowed: {
    type: Number,
    default: 15,
  },
  allowSupportRequest: Boolean,
  supportRequestType: String,
});

const search = ref("");
const searchResults = ref([]);
const currentPage = ref(1);
const loading = ref(false);
const showSupportRequestModal = ref(false);
const store = useAccountStore();

const openSupportRequest = () => showSupportRequestModal.value = true;

const handleSearch = (page=1) => {
  loading.value = true;
  props.getOptions({page: page, search: search.value}).then((response) => {
    if(page === 1) searchResults.value = [];
    searchResults.value.push(...response.data.map((item) => item.name));
    currentPage.value = response.meta.current_page;
  }).finally(() => {
    loading.value = false;
  });
};

const debouncedSearch = debounce(handleSearch, 250);

watch(() => search.value, () => debouncedSearch(1));
onMounted(() => handleSearch());
const updateModelValue = (value) => {
  if (props.modelValue.length >= props.maxAllowed && value.length > props.modelValue.length) {
    toast.warning(`You can only select up to ${props.maxAllowed} items`);
    return;
  }
  emits("update:modelValue", value);
};
</script>
